import type { OperationLogResponse } from './OperationLogResponse';
import type {
  OperationLogDocumentType,
  OperationLogDocumentTypeFilter,
  OperationLogEvent,
  OperationLogEventFilter,
  OperationLogFilterOption,
} from './operation-log.types';

const OPERATION_LOG_TOTAL = 1000;
const BASE_OCCURRED_AT = '2025-09-30T15:03:00.000Z';

const DOCUMENT_TYPES: OperationLogDocumentType[] = [
  'Documento Externo',
  'Registros',
  'Manual da Qualidade',
  'Procedimento',
  'Instrução de Trabalho',
  'Dados',
  '-',
];

const EVENTS: OperationLogEvent[] = [
  'Visualização',
  'Inclusão',
  'Alteração',
  'Login',
  'Acesso Negado',
  'Deleção',
];

const COLLABORATORS = [
  {
    name: 'Fabio Ferreira Netto',
    roles: [
      'Gerente da Qualidade',
      'Analista de Processos',
      'Auditor Interno',
      'Coordenador de Documentação',
    ],
  },
  { name: 'Luiz Carlos Silva', roles: ['Almoxarife Jr'] },
  {
    name: 'Augusto Nunes',
    roles: ['Analista de Certificação e Fomento Sr', 'Analista de Garantia da Qualidade'],
  },
  { name: 'Roberto Melo', roles: ['Técnico de Processos em Pesquisa e Desenvolvimento'] },
  { name: 'Rodrigo Sampaio dos Santos', roles: ['Técnico de Segurança no Trabalho Sr'] },
  {
    name: 'João Gomes',
    roles: ['Especialista em Treinamento e Aperfeiçoamento Técnico Científico'],
  },
  { name: 'Sara Fernandes', roles: [] },
  { name: 'Bianca Pereira', roles: ['Analista de Garantia da Qualidade'] },
].map((collaborator) => ({
  ...collaborator,
  role: collaborator.roles.length > 0 ? collaborator.roles.join(', ') : '-',
}));

const IPS = ['255.255.255.255', '192.168.0.25', '10.0.0.18', '172.16.20.44', '200.152.12.8'];

const DOCUMENT_CODES = ['IT.00XXXXXXXXX', 'DA.03.35', 'RG.01.02', 'MQ.01', 'PR.04.18', 'DG.22.07'];

const DESCRIPTION_BY_EVENT: Record<OperationLogEvent, (documentCode: string) => string> = {
  Visualização: (documentCode) => `Visualização do documento ${documentCode}`,
  Inclusão: (documentCode) => `Inclusão do documento ${documentCode} no sistema`,
  Alteração: (documentCode) => `Alteração do documento ${documentCode}`,
  Login: () => 'Tentativa de Login com acesso liberado',
  'Acesso Negado': () => 'Tentativa de Login com acesso negado',
  Deleção: (documentCode) => `Deleção do documento ${documentCode}`,
};

export const OPERATION_LOG_DOCUMENT_TYPE_OPTIONS = DOCUMENT_TYPES;
export const OPERATION_LOG_EVENT_OPTIONS = EVENTS;
export const OPERATION_LOG_DOCUMENT_TYPE_FILTER_OPTIONS: OperationLogFilterOption<OperationLogDocumentTypeFilter>[] =
  [
    { value: 'INTERNAL', label: 'Documento Interno' },
    { value: 'DE', label: 'Documento Externo' },
    { value: 'RG', label: 'Registros' },
    { value: 'MQ', label: 'Manual da Qualidade' },
    { value: 'PR', label: 'Procedimento' },
    { value: 'IT', label: 'Instrução de Trabalho' },
    { value: 'DA', label: 'Dados' },
  ];
export const OPERATION_LOG_EVENT_FILTER_OPTIONS: OperationLogFilterOption<OperationLogEventFilter>[] =
  [
    { value: 'LOGIN_SUCCESS', label: 'Login' },
    { value: 'LOGIN_FAILED', label: 'Acesso Negado' },
    { value: 'VIEW', label: 'Visualização' },
    { value: 'CREATE', label: 'Inclusão' },
    { value: 'UPDATE', label: 'Alteração' },
    { value: 'DELETE', label: 'Deleção' },
  ];

const BASE_OPERATION_LOGS: OperationLogResponse[] = [
  {
    id: 1,
    occurredAt: BASE_OCCURRED_AT,
    collaborator: COLLABORATORS[0],
    ipAddress: IPS[0],
    documentType: 'Instrução de Trabalho',
    event: 'Visualização',
    description: 'Visualização do documento IT.00XXXXXXXXX XXXXXXXXXXXXXXXXXXXXXXXXXX',
  },
  {
    id: 2,
    occurredAt: BASE_OCCURRED_AT,
    collaborator: COLLABORATORS[1],
    ipAddress: IPS[0],
    documentType: 'Dados',
    event: 'Inclusão',
    description:
      'Inclusão do Documento DA.03.35 - Banner BEAUTY DERMO MAXX Kit Marketing R01-010202.pdf',
  },
  {
    id: 3,
    occurredAt: BASE_OCCURRED_AT,
    collaborator: COLLABORATORS[2],
    ipAddress: IPS[0],
    documentType: 'Registros',
    event: 'Alteração',
    description: 'Alteração do Registro RG.01.02 - Controle de SAC',
  },
  {
    id: 4,
    occurredAt: BASE_OCCURRED_AT,
    collaborator: COLLABORATORS[3],
    ipAddress: IPS[0],
    documentType: '-',
    event: 'Login',
    description: 'Tentativa de Login com acesso liberado',
  },
  {
    id: 5,
    occurredAt: BASE_OCCURRED_AT,
    collaborator: COLLABORATORS[4],
    ipAddress: IPS[0],
    documentType: 'Instrução de Trabalho',
    event: 'Alteração',
    description: 'Alteração do documento IT.00XXXXXXXXX XXXXXXXXXXXXXXXXXXXXXXXXXX',
  },
  {
    id: 6,
    occurredAt: BASE_OCCURRED_AT,
    collaborator: COLLABORATORS[5],
    ipAddress: IPS[0],
    documentType: '-',
    event: 'Acesso Negado',
    description: 'Tentativa de Login com acesso negado',
  },
  {
    id: 7,
    occurredAt: BASE_OCCURRED_AT,
    collaborator: COLLABORATORS[6],
    ipAddress: IPS[0],
    documentType: 'Documento Externo',
    event: 'Deleção',
    description: 'Deleção de prova do Documento DE.02.03 - Certificações',
  },
];

function createOperationLogFromSeed(index: number): OperationLogResponse {
  const collaborator = COLLABORATORS[index % COLLABORATORS.length];
  const documentType = DOCUMENT_TYPES[index % DOCUMENT_TYPES.length];
  const event = EVENTS[index % EVENTS.length];
  const documentCode = DOCUMENT_CODES[index % DOCUMENT_CODES.length];
  const baseDate = new Date(BASE_OCCURRED_AT);

  baseDate.setMinutes(baseDate.getMinutes() - index * 7);

  return {
    id: index + 1,
    occurredAt: baseDate.toISOString(),
    collaborator,
    ipAddress: IPS[index % IPS.length],
    documentType,
    event,
    description: DESCRIPTION_BY_EVENT[event](documentCode),
  };
}

export function getOperationLogMocks(total = OPERATION_LOG_TOTAL): OperationLogResponse[] {
  if (total <= BASE_OPERATION_LOGS.length) {
    return BASE_OPERATION_LOGS.slice(0, total);
  }

  const generatedLogs = [...BASE_OPERATION_LOGS];

  for (let index = BASE_OPERATION_LOGS.length; index < total; index += 1) {
    generatedLogs.push(createOperationLogFromSeed(index));
  }

  return generatedLogs;
}

export const OPERATION_LOG_MOCKS = getOperationLogMocks();
